import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { CgSpinner } from "react-icons/cg";
import { useNavigate } from "react-router-dom";
import axios from "../utils/axiosConfig";

const SettingsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState({
    name: "",
    role: "",
    lastLogin: "",
    companyName: "",
  });

  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [extraFields, setExtraFields] = useState([null, null, null, null]); // Initialize with 4 fields as null
  const [nonEditableFields, setNonEditableFields] = useState([
    false,
    false,
    false,
    false,
  ]); // Track non-editable status
  const [fieldsFromServer, setFieldsFromServer] = useState([
    false,
    false,
    false,
    false,
  ]); // Track fields that came from server response

  const [currentExtraFieldProducts, setCurrentExtraFieldProducts] = useState(
    []
  ); // Store current extra fields for products
  const [currentExtraFieldBatches, setCurrentExtraFieldBatches] = useState([]); // Store current extra fields for batches

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get("/protected");
        const { name, role, last_login, company_name } = response.data;
        setUserData({
          name,
          role,
          lastLogin: last_login,
          companyName: company_name,
        });
        if (role !== "master") {
          navigate("/dashboard");
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        navigate("/login");
      }
    };
    fetchUserData();
  }, [navigate]);

  // Fetch existing extra fields for Products or Batches
  const fetchExtraFields = async (type) => {
    try {
      const response = await axios.get("/company-extra-fields");
      const { extra_field_products, extra_field_batch } = response.data;

      setCurrentExtraFieldProducts(extra_field_products || []);
      setCurrentExtraFieldBatches(extra_field_batch || []);

      if (type === "product") {
        const fields = [
          extra_field_products[0] || null,
          extra_field_products[1] || null,
          extra_field_products[2] || null,
          extra_field_products[3] || null,
        ];
        setExtraFields(fields);

        // Set non-editable and from-server status for fields with values
        setNonEditableFields(fields.map((field) => !!field));
        setFieldsFromServer(fields.map((field) => !!field));
      } else if (type === "batch") {
        const fields = [
          extra_field_batch[0] || null,
          extra_field_batch[1] || null,
          extra_field_batch[2] || null,
          extra_field_batch[3] || null,
        ];
        setExtraFields(fields);

        // Set non-editable and from-server status for fields with values
        setNonEditableFields(fields.map((field) => !!field));
        setFieldsFromServer(fields.map((field) => !!field));
      }
    } catch (error) {
      console.error("Error fetching extra fields:", error);
    }
  };

  // Handlers to open/close modals
  const openProductModal = async () => {
    await fetchExtraFields("product"); // Fetch the product extra fields
    setIsProductModalOpen(true);
  };
  const closeProductModal = () => setIsProductModalOpen(false);

  const openBatchModal = async () => {
    await fetchExtraFields("batch"); // Fetch the batch extra fields
    setIsBatchModalOpen(true);
  };
  const closeBatchModal = () => setIsBatchModalOpen(false);

  // Handle form input changes
  const handleInputChange = (index, value) => {
    const updatedFields = [...extraFields];
    updatedFields[index] = value || null; // Set null if value is empty
    setExtraFields(updatedFields);
  };

  // Edit field: make it editable
  const handleEditField = (index) => {
    const updatedEditableFields = [...nonEditableFields];
    updatedEditableFields[index] = false; // Make the field editable
    setNonEditableFields(updatedEditableFields);
  };

  // Remove field: set value to null and make editable
  const handleRemoveField = (index) => {
    const updatedFields = [...extraFields];
    updatedFields[index] = null; // Set the value to null

    const updatedEditableFields = [...nonEditableFields];
    updatedEditableFields[index] = false; // Make the field editable

    setExtraFields(updatedFields);
    setNonEditableFields(updatedEditableFields);
  };

  // Save updated extra fields
  const handleSaveExtraFields = async (type) => {
    try {
      // Filter out empty fields (null) from the extraFields array before saving
      const filteredFields = extraFields.filter((field) => field !== null);

      // Ensure that we send the correct fields based on type (product or batch)
      const payload = {
        extra_field_products:
          type === "product" ? filteredFields : currentExtraFieldProducts, // Preserve current batch fields when updating products
        extra_field_batch:
          type === "batch" ? filteredFields : currentExtraFieldBatches, // Preserve current product fields when updating batches
      };

      const response = await axios.post("/update-extra-fields", payload);
      console.log("Response after saving:", response.data);
      alert("Extra fields updated successfully.");
      closeProductModal();
      closeBatchModal();
    } catch (error) {
      console.error("Error saving extra fields:", error);
      alert("An error occurred while updating extra fields.");
    }
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar userData={userData} />
      <div className="flex-1">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <CgSpinner className="animate-spin text-4xl" />
          </div>
        ) : (
          <>
            <Header userData={userData} />
            <div className="p-6">
              <h1 className="text-xl font-semibold mb-6">Settings</h1>

              {/* Card Buttons */}
              <div className="grid grid-cols-2 gap-6">
                <div
                  onClick={openProductModal}
                  className="p-6 bg-white rounded-lg shadow-md cursor-pointer hover:shadow-lg"
                >
                  <h2 className="text-lg font-bold">
                    Add Extra Fields for Products
                  </h2>
                </div>
                <div
                  onClick={openBatchModal}
                  className="p-6 bg-white rounded-lg shadow-md cursor-pointer hover:shadow-lg"
                >
                  <h2 className="text-lg font-bold">
                    Add Extra Fields for Batches
                  </h2>
                </div>
              </div>

              {/* Modal for Extra Fields for Products */}
              {isProductModalOpen && (
                <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
                  <div className="bg-white p-8 rounded-lg shadow-lg w-96">
                    <h2 className="text-lg font-bold mb-4">
                      Add Extra Fields for Products
                    </h2>
                    <form>
                      {extraFields.map((field, index) => (
                        <div key={index} className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Additional Field {index + 1}
                          </label>
                          <input
                            type="text"
                            name={`field${index + 1}`}
                            value={field || ""}
                            onChange={(e) =>
                              handleInputChange(index, e.target.value)
                            }
                            className="block w-full p-2 border border-gray-300 rounded"
                            readOnly={nonEditableFields[index]} // Make field editable based on nonEditableFields
                          />
                          {/* Show "Edit" and "Remove" only for fields that came from server */}
                          {fieldsFromServer[index] && (
                            <div className="mt-2">
                              <button
                                type="button"
                                className="text-blue-600 hover:underline mr-4"
                                onClick={() => handleEditField(index)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="text-red-600 hover:underline"
                                onClick={() => handleRemoveField(index)}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={closeProductModal}
                          className="mr-4 px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveExtraFields("product")}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Save
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Modal for Extra Fields for Batches */}
              {isBatchModalOpen && (
                <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
                  <div className="bg-white p-8 rounded-lg shadow-lg w-96">
                    <h2 className="text-lg font-bold mb-4">
                      Add Extra Fields for Batches
                    </h2>
                    <form>
                      {extraFields.map((field, index) => (
                        <div key={index} className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Additional Field {index + 1}
                          </label>
                          <input
                            type="text"
                            name={`field${index + 1}`}
                            value={field || ""}
                            onChange={(e) =>
                              handleInputChange(index, e.target.value)
                            }
                            className="block w-full p-2 border border-gray-300 rounded"
                            readOnly={nonEditableFields[index]} // Make field editable based on nonEditableFields
                          />
                          {/* Show "Edit" and "Remove" only for fields that came from server */}
                          {fieldsFromServer[index] && (
                            <div className="mt-2">
                              <button
                                type="button"
                                className="text-blue-600 hover:underline mr-4"
                                onClick={() => handleEditField(index)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="text-red-600 hover:underline"
                                onClick={() => handleRemoveField(index)}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={closeBatchModal}
                          className="mr-4 px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveExtraFields("batch")}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Save
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
